import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getTopics, getTopicTypes, getTopicById, updateTopic, deleteTopic } from "wasp/client/operations";
import { Filter, Plus, Upload } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../hooks/use-toast";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { topicConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

export default function TopicsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: topicTypes = [] } = useQuery(getTopicTypes);
  const { data: topics = [], isLoading, error, refetch } = useQuery(getTopics);
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [slugFilter, setSlugFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    name: '',
    slug: '',
    topicTypeId: '',
    description: '',
    content: '',
    parentTopicId: '' as string | null,
    isActive: true
  });

  // Fetch selected topic details for flyout
  const { data: selectedTopic, isLoading: loadingTopic } = useQuery(
    getTopicById,
    { id: selectedTopicId || '' },
    { enabled: !!selectedTopicId, keepPreviousData: true }
  );

  const filteredData = useMemo(() => {
    return (topics as any[]).filter((topic: any) => {
      if (searchTerm && !topic.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (slugFilter && !topic.slug.toLowerCase().includes(slugFilter.toLowerCase())) {
        return false;
      }
      if (selectedTypeId && topic.topicTypeId !== selectedTypeId) {
        return false;
      }
      return true;
    });
  }, [topics, searchTerm, slugFilter, selectedTypeId]);

  const hasData = (topics as any[]).length > 0;

  // Initialize edit fields when topic is selected
  useEffect(() => {
    if (selectedTopic) {
      const topic = selectedTopic as any;
      setEditFormData({
        name: topic.name || '',
        slug: topic.slug || '',
        topicTypeId: topic.topicTypeId || '',
        description: topic.description || '',
        content: topic.content || '',
        parentTopicId: topic.parentTopicId || null,
        isActive: topic.isActive ?? true
      });
    }
  }, [selectedTopic]);

  // Check if any changes have been made
  const topicData = selectedTopic as any;
  const hasChanges = topicData ? (
    editFormData.name !== (topicData.name || '') ||
    editFormData.slug !== (topicData.slug || '') ||
    editFormData.topicTypeId !== (topicData.topicTypeId || '') ||
    editFormData.description !== (topicData.description || '') ||
    editFormData.content !== (topicData.content || '') ||
    editFormData.parentTopicId !== (topicData.parentTopicId || null) ||
    editFormData.isActive !== (topicData.isActive ?? true)
  ) : false;

  const handleTopicClick = (topicId: string) => {
    setSelectedTopicId(topicId);
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!topicData) return;

    try {
      await updateTopic({
        id: topicData.id,
        ...editFormData,
        parentTopicId: editFormData.parentTopicId || undefined
      });

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "Topic updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update topic",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!topicData) return;
    if (confirm('Are you sure you want to delete this topic? This action cannot be undone.')) {
      try {
        await deleteTopic({ id: topicData.id });
        setSelectedTopicId(null);
        await refetch();
        toast({
          title: "Success",
          description: "Topic deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: 'Failed to delete topic: ' + (error as Error).message,
          variant: "destructive",
        });
      }
    }
  };

  // Navigation helpers
  const currentTopicIndex = filteredData.findIndex(t => t.id === selectedTopicId) ?? -1;
  const hasPrevious = currentTopicIndex > 0;
  const hasNext = currentTopicIndex >= 0 && currentTopicIndex < filteredData.length - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && filteredData) {
      const previousTopic = filteredData[currentTopicIndex - 1];
      setSelectedTopicId(previousTopic.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && filteredData) {
      const nextTopic = filteredData[currentTopicIndex + 1];
      setSelectedTopicId(nextTopic.id);
    }
  };

  // Get list of potential parent topics
  const availableParentTopics = (topics as any[]).filter((topic: any) =>
    topic.id !== selectedTopicId && // Cannot be its own parent
    topic.parentTopicId !== selectedTopicId // Cannot be a child of this topic
  );

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Topic Names</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage Community knowledge topics ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk Upload"
                    variant="secondary"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/topics/new')}
                  icon={Plus}
                  label="Create Topic"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className="h-20" />

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'Name', span: 3 },
              { title: 'Slug', span: 2 },
              { title: 'Topic Type', span: 2 },
              { title: 'Description', span: 3 },
              { title: 'Services', span: 2 }
            ]}
            filters={[
              {
                span: 3,
                content: (
                  <input
                      type="text"
                      placeholder="Search name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                )
              },
              {
                span: 2,
                content: (
                  <input
                    type="text"
                    placeholder="Filter by slug..."
                    value={slugFilter}
                    onChange={(e) => setSlugFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )
              },
              {
                span: 2,
                content: (
                  <select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {(topicTypes as any[]).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )
              },
              { span: 3, content: <div className="h-10" /> },
              { span: 2, content: <div className="h-10" /> }
            ]}
          />
        )}

        {/* Content */}
        <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading topics: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No topics found</p>
                <p className="text-sm mt-2">
                  {searchTerm || slugFilter || selectedTypeId ? "Try adjusting your filters" : "Click 'Create Topic' to add your first topic"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((topic: any) => (
                  <div
                    key={topic.id}
                    className="px-4 py-2.5 admin-hoverable transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => handleTopicClick(topic.id)}
                    title="Click to view details"
                  >
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                        {topic.name}
                      </div>
                      {topic.isFeatured && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {topic.slug}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {topic.topicType?.label || '-'}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                        {topic.description || '-'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {(topic as any)._count?.topicServices || 0} services
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedTopicId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTopicId(null);
              setFlyoutMode('view');
            }
          }}
          title={flyoutMode === 'view' ? 'View Topic' : 'Edit Topic'}
          description={topicData?.slug || undefined}
          width="2xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === 'view' ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode('view')}
                  className="w-[110px]"
                >
                  {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingTopic && !selectedTopic ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : topicData ? (
            <>
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Name"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.name}
                          onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter topic name"
                        />
                      ) : (
                        <span>{topicData.name}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Slug"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.slug}
                          onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter URL slug"
                        />
                      ) : (
                        <span className="text-xs text-gray-600">{topicData.slug}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Topic Type"
                    value={
                      flyoutMode === 'edit' ? (
                        <Select
                          value={editFormData.topicTypeId}
                          onValueChange={(value) => setEditFormData({ ...editFormData, topicTypeId: value })}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="Select a topic type" />
                          </SelectTrigger>
                          <SelectContent>
                            {(topicTypes as any[]).map((type: any) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{topicData.topicType?.label || '-'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter short description"
                        />
                      ) : (
                        <span>{topicData.description || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Content"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.content}
                          onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[120px]"
                          placeholder="Enter detailed content/guidance"
                        />
                      ) : (
                        <span className="whitespace-pre-wrap">{topicData.content || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Hierarchy Section */}
                <FlyoutGridSection title="HIERARCHY">
                  <FlyoutGridRow
                    label="Parent Topic"
                    value={
                      flyoutMode === 'edit' ? (
                        <Select
                          value={editFormData.parentTopicId || 'none'}
                          onValueChange={(value) => setEditFormData({ ...editFormData, parentTopicId: value === 'none' ? null : value })}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="None (Root Topic)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Root Topic)</SelectItem>
                            {availableParentTopics.map((topic: any) => (
                              <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{topicData.parentTopic?.name || 'None (Root Topic)'}</span>
                      )
                    }
                  />
                  {topicData?.subTopics && topicData.subTopics.length > 0 && (
                    <FlyoutGridRow
                      label="Sub-Topics"
                      value={
                        <div className="flex flex-wrap gap-1">
                          {topicData.subTopics.map((child: any) => (
                            <span
                              key={child.id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {child.name}
                            </span>
                          ))}
                        </div>
                      }
                      isLast
                    />
                  )}
                </FlyoutGridSection>

                {/* Settings Section */}
                <FlyoutGridSection title="SETTINGS">
                  <FlyoutGridRow
                    label="Active Status"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isActive}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isActive: checked })}
                          />
                          <span className="text-sm">{editFormData.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <span className={topicData.isActive ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {topicData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === 'view' && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Linked Services (Legacy)"
                      value={<span>{topicData._count?.topicServices || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Linked Service Definitions"
                      value={<span>{topicData._count?.topicServiceDefinitions || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Linked Occasions"
                      value={<span>{topicData._count?.topicOccasions || 0}</span>}
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            </>
          ) : null}
        </AdminFlyoutPane>
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={topicConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



